import React, { useState, useEffect } from 'react';
import { 
  Plus, Trash2, Calendar as CalendarIcon, 
  Settings, ChevronLeft, ChevronRight, Printer, 
  X, LayoutGrid, LogOut, LogIn, AlertCircle, Edit2,
  Sun, Moon
} from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { 
  getFirestore, collection, doc, onSnapshot, 
  setDoc, deleteDoc 
} from 'firebase/firestore';
import { 
  getAuth, GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged 
} from 'firebase/auth';

// --- Firebase Configuration (Using Environment Variables) ---
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const DEFAULT_CONFIG = {
  totalPtoHours: 120,
  totalFloatHours: 16,
  darkMode: false,
  holidays: {
    '2026-01-01': "New Year's Day",
    '2026-01-19': "MLK Jr. Day",
    '2026-02-16': "Presidents' Day",
    '2026-05-25': "Memorial Day",
    '2026-06-19': "Juneteenth",
    '2026-07-04': "Independence Day",
    '2026-09-07': "Labor Day",
    '2026-11-11': "Veterans Day",
    '2026-11-26': "Thanksgiving",
    '2026-12-25': "Christmas Day",
  }
};

const isConfigValid = !!firebaseConfig.apiKey && firebaseConfig.apiKey !== "undefined";

let app, auth, db, googleProvider;
if (isConfigValid) {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  db = getFirestore(app);
  googleProvider = new GoogleAuthProvider();
}

const appId = 'pto-tracker-multiyr';

const App = () => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [view, setView] = useState('calendar');
  const [sidebarTab, setSidebarTab] = useState('upcoming');
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [hoveredDate, setHoveredDate] = useState(null);
  
  const [ptoEntries, setPtoEntries] = useState([]);
  const [floatEntries, setFloatEntries] = useState([]);
  const [travelEntries, setTravelEntries] = useState([]);
  const [config, setConfig] = useState(DEFAULT_CONFIG);

  const [newEntry, setNewEntry] = useState({ date: '', reason: '', type: 'PTO', duration: 'full' });
  const [editingId, setEditingId] = useState(null);
  const [isAddingHoliday, setIsAddingHoliday] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ date: '', name: '' });

  const todayStr = new Date().toISOString().split('T')[0];

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split('-');
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  };

  useEffect(() => {
    if (!isConfigValid) return;
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setAuthLoading(false);
      if (window.innerWidth >= 768) setIsSidebarOpen(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (config.darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [config.darkMode]);

  useEffect(() => {
    if (!user || !isConfigValid) return;

    const ptoRef = collection(db, 'artifacts', appId, 'users', user.uid, 'ptoEntries');
    const floatRef = collection(db, 'artifacts', appId, 'users', user.uid, 'floatEntries');
    const travelRef = collection(db, 'artifacts', appId, 'users', user.uid, 'travelEntries');
    const configRef = doc(db, 'artifacts', appId, 'users', user.uid, 'settings', 'config');

    const unsubPto = onSnapshot(ptoRef, (snap) => setPtoEntries(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubFloat = onSnapshot(floatRef, (snap) => setFloatEntries(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const unsubTravel = onSnapshot(travelRef, (snap) => setTravelEntries(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

    const unsubConfig = onSnapshot(configRef, (docSnap) => {
      if (docSnap.exists()) {
        setConfig(docSnap.data());
      } else {
        setDoc(configRef, DEFAULT_CONFIG);
      }
    }, (err) => console.error("Config Fetch Error:", err));

    return () => {
      unsubPto();
      unsubFloat();
      unsubTravel();
      unsubConfig();
    };
  }, [user]);

  const login = async () => {
    try { await signInWithPopup(auth, googleProvider); } catch (e) { console.error(e); }
  };

  const logout = async () => {
    try { await signOut(auth); setPtoEntries([]); setFloatEntries([]); setTravelEntries([]); } catch (e) { console.error(e); }
  };

  const addEntry = async () => {
    if (!user || !newEntry.date || !newEntry.reason) return;
    const entryData = {
      date: newEntry.date,
      reason: newEntry.reason,
      hrs: newEntry.duration === 'full' ? 8 : 4
    };
    
    let collectionName = 'ptoEntries';
    if (newEntry.type === 'FLOAT') collectionName = 'floatEntries';
    if (newEntry.type === 'TRAVEL') collectionName = 'travelEntries';

    const docRef = editingId 
      ? doc(db, 'artifacts', appId, 'users', user.uid, collectionName, editingId)
      : doc(collection(db, 'artifacts', appId, 'users', user.uid, collectionName));
    
    await setDoc(docRef, entryData);
    setNewEntry({ ...newEntry, date: '', reason: '' });
    setEditingId(null);
    if (window.innerWidth < 768) setIsSidebarOpen(false);
  };

  const startEdit = (entry) => {
    setNewEntry({ date: entry.date, reason: entry.reason, type: entry.type, duration: entry.hrs === 8 ? 'full' : 'half' });
    setEditingId(entry.id);
    setIsSidebarOpen(true);
  };

  const updateConfig = async (newConfig) => {
    if (!user) return;
    const configRef = doc(db, 'artifacts', appId, 'users', user.uid, 'settings', 'config');
    await setDoc(configRef, newConfig);
  };

  const toggleDarkMode = () => {
    updateConfig({ ...config, darkMode: !config.darkMode });
  };

  const deleteEntry = async (id, type) => {
    if (!user) return;
    let collectionName = 'ptoEntries';
    if (type === 'FLOAT') collectionName = 'floatEntries';
    if (type === 'TRAVEL') collectionName = 'travelEntries';
    
    await deleteDoc(doc(db, 'artifacts', appId, 'users', user.uid, collectionName, id));
    if (editingId === id) setNewEntry({ ...newEntry, date: '', reason: '' });
  };

  const handleAddHoliday = async () => {
    if (!newHoliday.date || !newHoliday.name) return;
    const updatedHolidays = { ...config.holidays, [newHoliday.date]: newHoliday.name };
    await updateConfig({ ...config, holidays: updatedHolidays });
    setNewHoliday({ date: '', name: '' });
    setIsAddingHoliday(false);
  };

  const ptoThisYear = ptoEntries.filter(e => e.date.startsWith(currentYear.toString()));
  const floatThisYear = floatEntries.filter(e => e.date.startsWith(currentYear.toString()));
  const usedPtoHours = ptoThisYear.reduce((sum, e) => sum + e.hrs, 0);
  const usedFloatHours = floatThisYear.reduce((sum, e) => sum + e.hrs, 0);

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const getDaysInMonth = (m, y) => new Date(y, m + 1, 0).getDate();
  const getFirstDayOfMonth = (m, y) => new Date(y, m, 1).getDay();

  const getEntryForDate = (dateStr) => {
    const pto = ptoEntries.find(e => e.date === dateStr);
    if (pto) return { type: 'PTO', reason: pto.reason, hrs: pto.hrs };
    const float = floatEntries.find(e => e.date === dateStr);
    if (float) return { type: 'FLOAT', reason: float.reason, hrs: float.hrs };
    const travel = travelEntries.find(e => e.date === dateStr);
    if (travel) return { type: 'TRAVEL', reason: travel.reason, hrs: travel.hrs };
    if (config.holidays[dateStr]) return { type: 'HOLIDAY', reason: config.holidays[dateStr], hrs: 8 };
    return null;
  };

  if (!isConfigValid) return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-950 p-6 text-center">
      <div className="bg-white dark:bg-slate-900 p-8 rounded-3xl shadow-xl border border-red-100 dark:border-red-900 max-w-lg w-full">
        <AlertCircle size={48} className="text-red-500 mx-auto mb-6" />
        <h1 className="text-xl font-bold dark:text-slate-100">Firebase Error</h1>
        <p className="text-slate-600 dark:text-slate-400 mt-2">API key missing in environment variables.</p>
      </div>
    </div>
  );

  if (authLoading) return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-950 gap-4">
      <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      <p className="text-slate-500 font-medium">Syncing...</p>
    </div>
  );

  if (!user) return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-950 p-4 text-center">
      <div className="bg-white dark:bg-slate-900 p-10 rounded-3xl shadow-xl border border-slate-200 dark:border-slate-800 max-w-sm w-full">
        <CalendarIcon size={48} className="text-blue-600 mx-auto mb-6" />
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">PTO Tracker</h1>
        <p className="text-slate-500 dark:text-slate-400 mb-8 text-sm">Secure your data with Google Authentication.</p>
        <button onClick={login} className="flex items-center justify-center gap-3 w-full bg-slate-900 dark:bg-blue-600 hover:opacity-90 text-white font-bold py-3 px-6 rounded-xl transition-all shadow-lg text-sm md:text-base">
          <LogIn size={20} /> Sign in with Google
        </button>
      </div>
    </div>
  );

  return (
    <div className="h-screen bg-slate-50 dark:bg-slate-950 flex flex-col font-sans overflow-hidden transition-colors duration-300 print:bg-white print:overflow-visible">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-4 md:px-6 py-3 md:py-4 flex items-center justify-between sticky top-0 z-40 print:hidden shadow-sm">
        <div className="flex items-center gap-3 md:gap-4 overflow-hidden">
          <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-500 dark:text-slate-400 transition-colors flex-shrink-0">
            {isSidebarOpen ? <X size={20} /> : <LayoutGrid size={20} />}
          </button>
          <div className="overflow-hidden">
            <div className="flex items-center gap-2 md:gap-3">
              <h1 className="text-base md:text-xl font-bold text-slate-900 dark:text-slate-100 truncate">PTO Tracker</h1>
              <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5 md:p-1 flex-shrink-0">
                <button onClick={() => setCurrentYear(prev => prev - 1)} className="p-0.5 md:p-1 hover:bg-white dark:hover:bg-slate-700 rounded transition-all dark:text-slate-400"><ChevronLeft size={14} /></button>
                <span className="px-1.5 md:px-3 text-[10px] md:text-sm font-bold text-slate-700 dark:text-slate-300 min-w-[40px] md:min-w-[60px] text-center">{currentYear}</span>
                <button onClick={() => setCurrentYear(prev => prev + 1)} className="p-0.5 md:p-1 hover:bg-white dark:hover:bg-slate-700 rounded transition-all dark:text-slate-400"><ChevronRight size={14} /></button>
              </div>
            </div>
            <div className="flex gap-2 md:gap-4 mt-0.5 md:mt-1.5 overflow-x-auto no-scrollbar whitespace-nowrap">
              <div className="flex flex-col flex-shrink-0">
                <span className="text-[8px] md:text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase leading-none mb-0.5">PTO</span>
                <span className="text-[10px] md:text-sm font-bold text-blue-600 dark:text-blue-400">{(config.totalPtoHours - usedPtoHours) / 8}d left</span>
              </div>
              <div className="w-px bg-slate-100 dark:bg-slate-800 self-stretch" />
              <div className="flex flex-col flex-shrink-0">
                <span className="text-[8px] md:text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase leading-none mb-0.5">Float</span>
                <span className="text-[10px] md:text-sm font-bold text-emerald-600 dark:text-emerald-400">{(config.totalFloatHours - usedFloatHours) / 8}d left</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-1 md:gap-2">
          <button onClick={toggleDarkMode} className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all" title="Toggle Theme">
            {config.darkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button onClick={() => setView(view === 'settings' ? 'calendar' : 'settings')} className={`p-2 rounded-lg transition-all ${view === 'settings' ? 'bg-blue-600 text-white shadow-md' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'}`} title="Settings">
            <Settings size={18} />
          </button>
          <button onClick={() => window.print()} className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 hidden md:block" title="Print">
            <Printer size={18} />
          </button>
          <div className="ml-1 md:ml-2 pl-1 md:pl-2 border-l border-slate-200 dark:border-slate-800">
            <button onClick={logout} className="p-2 text-slate-400 dark:text-slate-500 hover:text-red-500 transition-colors" title="Logout">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 flex overflow-hidden relative">
        {isSidebarOpen && (
          <div className="md:hidden absolute inset-0 bg-slate-900/40 dark:bg-black/60 backdrop-blur-sm z-40" onClick={() => setIsSidebarOpen(false)} />
        )}

        <aside className={`absolute md:relative inset-y-0 left-0 z-50 transition-all duration-300 overflow-hidden flex flex-col bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 shadow-xl md:shadow-none ${isSidebarOpen ? 'translate-x-0 w-[85vw] md:w-80' : '-translate-x-full md:w-0'}`}>
          <div className="p-5 md:p-6 space-y-6 overflow-y-auto flex-1 min-w-full">
            <div className="space-y-4">
              <h2 className="text-xs font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2 uppercase tracking-widest">
                {editingId ? <Edit2 size={14} className="text-amber-500" /> : <Plus size={14} className="text-blue-500" />} {editingId ? 'Edit Entry' : 'Add Entry'}
              </h2>
              <div className="flex p-0.5 bg-slate-100 dark:bg-slate-800 rounded-xl">
                {['full', 'half'].map(d => (
                  <button key={d} onClick={() => setNewEntry({...newEntry, duration: d})} className={`flex-1 py-2 text-[10px] font-bold rounded-lg uppercase transition-all ${newEntry.duration === d ? 'bg-white dark:bg-slate-700 shadow-sm text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-slate-500'}`}>{d} Day</button>
                ))}
              </div>
              <div className="grid grid-cols-1 gap-3">
                <select className="p-3 text-sm bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 dark:text-slate-100 appearance-none" value={newEntry.type} onChange={e => setNewEntry({...newEntry, type: e.target.value})}>
                  <option value="PTO">General PTO</option>
                  <option value="FLOAT">Floating Holiday</option>
                  <option value="TRAVEL">Work Travel</option>
                </select>
                <input type="date" className="p-3 text-sm bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 dark:text-slate-100" value={newEntry.date} onChange={e => setNewEntry({...newEntry, date: e.target.value})} />
                <input type="text" placeholder="Reason (e.g. London Office)" className="p-3 text-sm bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 dark:text-slate-100 placeholder-slate-400" value={newEntry.reason} onChange={e => setNewEntry({...newEntry, reason: e.target.value})} />
                <div className="flex gap-2">
                  <button onClick={addEntry} className={`flex-1 ${editingId ? 'bg-amber-500' : 'bg-blue-600'} text-white font-bold py-3 rounded-xl transition-all shadow-md active:scale-95`}>{editingId ? 'Update' : 'Save'}</button>
                  {editingId && <button onClick={() => { setNewEntry({ ...newEntry, date: '', reason: '' }); setEditingId(null); }} className="px-4 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 font-bold rounded-xl transition-all active:scale-95">Cancel</button>}
                </div>
              </div>
            </div>

            <div className="space-y-4 pt-6 border-t border-slate-100 dark:border-slate-800">
              <div className="flex gap-1 p-0.5 bg-slate-100 dark:bg-slate-800 rounded-xl">
                {['upcoming', 'past'].map(t => (
                  <button key={t} onClick={() => setSidebarTab(t)} className={`flex-1 py-2 text-[10px] font-bold rounded-lg uppercase transition-all ${sidebarTab === t ? 'bg-white dark:bg-slate-700 shadow-sm text-blue-600 dark:text-blue-400' : 'text-slate-400 dark:text-slate-500'}`}>{t}</button>
                ))}
              </div>
              <div className="space-y-2">
                {[...ptoEntries.map(e => ({...e, type: 'PTO'})), ...floatEntries.map(e => ({...e, type: 'FLOAT'})), ...travelEntries.map(e => ({...e, type: 'TRAVEL'}))]
                  .sort((a, b) => sidebarTab === 'upcoming' ? a.date.localeCompare(b.date) : b.date.localeCompare(a.date))
                  .filter(e => sidebarTab === 'upcoming' ? e.date >= todayStr : e.date < todayStr)
                  .map(entry => (
                    <div key={`${entry.type}-${entry.id}`} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                      <div className="flex items-center gap-3 overflow-hidden">
                        <div className={`w-1 h-8 rounded-full flex-shrink-0 ${entry.type === 'FLOAT' ? 'bg-emerald-400' : entry.type === 'TRAVEL' ? 'bg-orange-500' : 'bg-blue-400'}`} />
                        <div className="overflow-hidden">
                          <div className="font-bold text-xs text-slate-800 dark:text-slate-200">{formatDisplayDate(entry.date)}</div>
                          <div className="text-[10px] text-slate-500 dark:text-slate-400 truncate uppercase font-medium">{entry.reason} • {entry.hrs}H</div>
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={() => startEdit(entry)} className="p-2 text-slate-300 dark:text-slate-600 hover:text-blue-500 transition-all"><Edit2 size={14} /></button>
                        <button onClick={() => deleteEntry(entry.id, entry.type)} className="p-2 text-slate-300 dark:text-slate-600 hover:text-red-500 transition-all"><Trash2 size={14} /></button>
                      </div>
                    </div>
                ))}
              </div>
            </div>
          </div>
        </aside>

        <section className="flex-1 overflow-y-auto p-4 md:p-8 bg-slate-50 dark:bg-slate-950 transition-colors duration-300 print:bg-white print:overflow-visible no-scrollbar">
          {view === 'calendar' ? (
            <div className="max-w-6xl mx-auto space-y-6 md:space-y-12 print:max-w-none print:space-y-0 print-calendar-container">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 print:grid-cols-3 print-grid-fix">
                {months.map((monthName, monthIdx) => {
                  const daysInMonth = getDaysInMonth(monthIdx, currentYear);
                  const firstDay = getFirstDayOfMonth(monthIdx, currentYear);
                  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
                  const blanks = Array.from({ length: firstDay }, (_, i) => i);

                  return (
                    <div key={monthName} className="bg-white dark:bg-slate-900 rounded-2xl p-4 md:p-5 border border-slate-100 dark:border-slate-800 shadow-sm print:rounded-none print:shadow-none print:border-slate-200 print:p-2 month-card">
                      <h3 className="font-extrabold text-slate-900 dark:text-slate-100 mb-3 md:mb-4 text-center tracking-tight uppercase text-[11px] md:text-sm border-b border-slate-50 dark:border-slate-800 pb-2 print:mb-1 print:pb-1 print:text-[10px]">{monthName}</h3>
                      <div className="grid grid-cols-7 gap-1 text-[8px] md:text-[9px] text-center font-bold text-slate-400 dark:text-slate-500 mb-2 print:mb-1 print:text-[7px]">
                        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => <div key={d}>{d}</div>)}
                      </div>
                      <div className="grid grid-cols-7 gap-1 print:gap-0.5">
                        {blanks.map(i => <div key={`b-${i}`} className="h-7 md:h-9 print:h-5" />)}
                        {days.map(day => {
                          const dateObj = new Date(currentYear, monthIdx, day);
                          const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;
                          const dateStr = `${currentYear}-${String(monthIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                          const isToday = dateStr === todayStr;
                          const entry = getEntryForDate(dateStr);
                          
                          let style = {}; 
                          let textColor = 'text-slate-600 dark:text-slate-400';
                          let cellClass = 'border border-slate-50 dark:border-slate-800';

                          if (entry) {
                            if (entry.type === 'PTO') {
                              style = entry.hrs === 4 ? { background: `linear-gradient(135deg, #3b82f6 50%, ${config.darkMode ? '#1e293b' : '#eff6ff'} 50%)` } : { backgroundColor: '#3b82f6' };
                              textColor = entry.hrs === 4 ? 'text-blue-900 dark:text-blue-100' : 'text-white';
                            } else if (entry.type === 'FLOAT') {
                              style = entry.hrs === 4 ? { background: `linear-gradient(135deg, #10b981 50%, ${config.darkMode ? '#1e293b' : '#ecfdf5'} 50%)` } : { backgroundColor: '#10b981' };
                              textColor = entry.hrs === 4 ? 'text-emerald-900 dark:text-emerald-100' : 'text-white';
                            } else if (entry.type === 'TRAVEL') {
                              style = entry.hrs === 4 ? { background: `linear-gradient(135deg, #f97316 50%, ${config.darkMode ? '#1e293b' : '#fff7ed'} 50%)` } : { backgroundColor: '#f97316' };
                              textColor = entry.hrs === 4 ? 'text-orange-900 dark:text-orange-100' : 'text-white';
                            } else if (entry.type === 'HOLIDAY') {
                              style = { backgroundColor: '#a855f7' }; textColor = 'text-white'; 
                            }
                          } else if (isToday) {
                            style = { backgroundColor: 'rgba(250, 204, 21, 0.3)' }; 
                          } else if (isWeekend) {
                            cellClass = 'bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-800';
                            textColor = 'text-slate-400 dark:text-slate-600';
                          }

                          return (
                            <div key={day} style={style} onMouseEnter={() => entry && setHoveredDate(dateStr)} onMouseLeave={() => setHoveredDate(null)}
                              className={`h-7 md:h-9 print:h-5 flex items-center justify-center rounded-lg text-[9px] md:text-[10px] font-bold print:border-slate-100 print:text-[8px] print:rounded-md relative group cursor-default transition-all duration-300 ${textColor} ${cellClass} ${isToday ? 'ring-2 ring-inset ring-slate-900 dark:ring-slate-100 shadow-sm' : ''}`}>
                              {day}
                              {hoveredDate === dateStr && entry && (
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-[140px] px-2 py-1.5 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-[9px] font-semibold rounded shadow-xl z-50 pointer-events-none animate-in fade-in zoom-in duration-100 print:hidden">
                                  <div className="line-clamp-3 text-center">{entry.reason}</div>
                                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900 dark:border-t-slate-100" />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="max-w-2xl mx-auto bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 border border-slate-200 dark:border-slate-800 shadow-sm space-y-8 print:hidden transition-colors duration-300">
              <section className="space-y-4">
                <h2 className="text-base md:text-lg font-bold text-slate-900 dark:text-slate-100 border-b dark:border-slate-800 pb-2">Allocations</h2>
                <div className="grid grid-cols-2 gap-4 md:gap-6">
                  {[['PTO', 'totalPtoHours'], ['Float', 'totalFloatHours']].map(([label, key]) => (
                    <div key={key}>
                      <label className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase mb-1 block">{label} Hours</label>
                      <input type="number" className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl outline-none text-slate-900 dark:text-slate-100" value={config[key]} onChange={e => updateConfig({...config, [key]: parseInt(e.target.value) || 0})} />
                    </div>
                  ))}
                </div>
              </section>

              <section className="space-y-4">
                <div className="flex justify-between items-center border-b dark:border-slate-800 pb-2">
                  <h2 className="text-base md:text-lg font-bold text-slate-900 dark:text-slate-100">National Holidays</h2>
                  {!isAddingHoliday && <button onClick={() => setIsAddingHoliday(true)} className="text-[10px] bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-3 py-1.5 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 font-bold transition-colors">+ Add</button>}
                </div>

                {isAddingHoliday && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-100 dark:border-blue-900/30 flex flex-col gap-3 animate-in slide-in-from-top duration-200">
                    <input type="date" className="p-3 text-sm bg-white dark:bg-slate-800 border border-blue-100 dark:border-slate-700 rounded-xl outline-none text-slate-900 dark:text-slate-100" value={newHoliday.date} onChange={e => setNewHoliday({ ...newHoliday, date: e.target.value })} />
                    <input type="text" placeholder="Holiday Name" className="p-3 text-sm bg-white dark:bg-slate-800 border border-blue-100 dark:border-slate-700 rounded-xl outline-none text-slate-900 dark:text-slate-100 placeholder-slate-400" value={newHoliday.name} onChange={e => setNewHoliday({ ...newHoliday, name: e.target.value })} />
                    <div className="flex gap-2">
                      <button onClick={handleAddHoliday} className="flex-1 py-3 bg-blue-600 dark:bg-blue-700 text-white rounded-xl font-bold">Add Holiday</button>
                      <button onClick={() => setIsAddingHoliday(false)} className="px-4 py-3 bg-white dark:bg-slate-700 text-slate-400 dark:text-slate-300 rounded-xl border border-blue-100 dark:border-slate-600">Cancel</button>
                    </div>
                  </div>
                )}

                <div className="space-y-2 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
                  {Object.entries(config.holidays).sort().map(([date, name]) => (
                    <div key={date} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl text-xs md:text-sm">
                      <div className="flex gap-4 items-center overflow-hidden"><span className="text-slate-400 dark:text-slate-500 font-mono flex-shrink-0">{date}</span><span className="text-slate-700 dark:text-slate-300 font-bold truncate">{name}</span></div>
                      <button onClick={() => { const h = {...config.holidays}; delete h[date]; updateConfig({...config, holidays: h}); }} className="p-2 text-slate-300 dark:text-slate-600 hover:text-red-500 transition-colors"><Trash2 size={16} /></button>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          )}
        </section>
      </main>

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @media print {
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          body { background: white !important; margin: 0 !important; padding: 0 !important; overflow: hidden !important; }
          .print-hidden { display: none !important; }
          @page { size: landscape; margin: 5mm; }
          .print-calendar-container { display: grid !important; grid-template-columns: repeat(3, 1fr) !important; grid-template-rows: repeat(4, 1fr) !important; height: 98vh !important; gap: 4px !important; padding: 0 !important; margin: 0 !important; }
          .print-grid-fix { display: contents !important; }
          .month-card { border: 1px solid #e2e8f0 !important; height: 100% !important; display: flex !important; flex-direction: column !important; background: white !important; }
          main, section { overflow: visible !important; height: 100% !important; }
        }
      `}</style>
    </div>
  );
};

export default App;